/**
 * @file EditExtractedInformationSkeleton.tsx
 *
 * @description This component renders a loading skeleton for the Edit Extracted Information page.
 * It mirrors the structure of the real edit form (video preview, section progress bar, form
 * fields, navigation, and submit area) so the layout does not shift once content arrives.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import { Box, Paper, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function EditExtractedInformationSkeleton
 *
 * @description A placeholder loading interface that structurally mirrors the actual
 * EditExtractedInformation form. Displayed while the edit form data is being fetched.
 *
 * The skeleton mirrors, in order:
 * - Video preview bar (rounded rectangle at top, outside the Paper)
 * - Section progress bar (a connected row of small circular step markers)
 * - Form heading + a responsive grid of field placeholders
 * - Form navigation (previous / next button-shaped placeholders)
 * - Submit area (a Paper block with a checkbox + text row and a full-width button)
 *
 * @returns {JSX.Element} A skeleton loading interface matching the edit form footprint.
 */
export const EditExtractedInformationSkeleton = () => {
  return (
    <Box>
      {/* Video preview - rounded bar at top (mirrors VideoPreview / its loading skeleton) */}
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
      </Box>

      <Grid container spacing={2}>
        {/* Main Form Area */}
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper sx={{ p: 3, position: "relative", borderRadius: 3 }}>
            {/* Section progress bar - a connected row of small circular step markers */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  position: "relative",
                }}
              >
                {/* Connecting track behind the markers */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 16,
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 2 }} />
                </Box>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    variant="circular"
                    width={32}
                    height={32}
                    sx={{ position: "relative", zIndex: 1 }}
                  />
                ))}
              </Box>
            </Box>

            {/* Main form content area */}
            <Box sx={{ pr: 2 }}>
              {/* Form section heading */}
              <Skeleton variant="text" width="40%" height={40} sx={{ mb: 3 }} />

              {/* Responsive grid of form fields */}
              <Grid container spacing={2}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                  </Grid>
                ))}
              </Grid>

              {/* Larger text-area field (e.g. notes / narrative) */}
              <Box sx={{ mt: 3 }}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>

            {/* Form navigation - previous / indicator / next */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                mt: 3,
                borderTop: "1px solid rgba(0,0,0,0.12)",
                pt: 3,
              }}
            >
              <Skeleton variant="rounded" width={120} height={40} />
              <Skeleton variant="text" width={48} height={32} />
              <Skeleton variant="rounded" width={120} height={40} />
            </Box>

            {/* Submit area - Paper block with confirmation checkbox + text and a button */}
            <Paper elevation={0} sx={{ mt: 4, p: 3, borderRadius: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <Skeleton variant="rectangular" width={24} height={24} sx={{ borderRadius: 0.5 }} />
                <Skeleton variant="text" width="60%" height={24} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Skeleton variant="rounded" width={240} height={52} />
              </Box>
            </Paper>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
